import type { SupabaseClient } from '@supabase/supabase-js';
import type { RegisterRole } from '@teslyn/domain';
import type { InviteLinkResult } from '@teslyn/domain';
import {
  isClientRole,
  normaliseTenantRole,
  sortTenantRoles,
  type ClientRole,
  type TenantRole,
} from '../access/roles.ts';

/** A staff member with every tenant role they hold (one `membership` row per role); their rights are the union. */
export interface StaffMember {
  readonly userId: string;
  readonly email: string | null;
  readonly displayName: string;
  /** In TENANT_ROLES order; never empty. */
  readonly roles: readonly TenantRole[];
  /** Raw database value per role (differs for the legacy `office_processor`), used to delete the right row. */
  readonly storedRoles: Readonly<Partial<Record<TenantRole, string>>>;
  /**
   * A system account (decision 540): only a platform admin is ever sent one (the database hides it from the
   * tenant), and the screen labels it "System".
   */
  readonly system: boolean;
}

export interface ClientMember {
  readonly userId: string;
  readonly email: string | null;
  readonly displayName: string;
  readonly role: ClientRole;
  readonly clientOrgId: string;
  readonly clientOrgName: string;
}

export interface ClientOrg {
  readonly id: string;
  readonly name: string;
}

export interface Members {
  readonly staff: readonly StaffMember[];
  readonly clients: readonly ClientMember[];
  readonly orgs: readonly ClientOrg[];
}

type UserEmbed = { readonly email: string; readonly display_name: string; readonly system?: boolean } | null;

function one<T>(v: T | T[] | null | undefined): T | null {
  return Array.isArray(v) ? (v[0] ?? null) : (v ?? null);
}

export async function loadMembers(sb: SupabaseClient, tenantId: string): Promise<Members> {
  const [m, c, o] = await Promise.all([
    sb
      .from('membership')
      .select('user_id, role, app_user:user_id (email, display_name, system)')
      .eq('tenant_id', tenantId),
    sb
      .from('client_membership')
      .select(
        'user_id, role, client_org_id, app_user:user_id (email, display_name), client_org:client_org_id!inner (id, name, tenant_id)',
      )
      .eq('client_org.tenant_id', tenantId),
    sb.from('client_org').select('id, name').eq('tenant_id', tenantId).is('deleted_at', null).order('name'),
  ]);
  for (const r of [m, c, o]) if (r.error) throw new Error(r.error.message);

  // One row per (user, role): gather each person's rows.
  const byUser = new Map<
    string,
    {
      email: string | null;
      displayName: string;
      system: boolean;
      stored: Partial<Record<TenantRole, string>>;
    }
  >();
  for (const r of (m.data ?? []) as unknown as {
    user_id: string;
    role: string;
    app_user: UserEmbed | UserEmbed[];
  }[]) {
    const role = normaliseTenantRole(r.role);
    if (!role) continue;
    const u = one(r.app_user);
    const entry = byUser.get(r.user_id) ?? {
      email: u?.email ?? null,
      displayName: u?.display_name ?? '',
      system: u?.system === true,
      stored: {},
    };
    entry.stored[role] = r.role;
    byUser.set(r.user_id, entry);
  }
  const staff: StaffMember[] = [...byUser].map(([userId, e]) => ({
    userId,
    email: e.email,
    displayName: e.displayName,
    roles: sortTenantRoles(Object.keys(e.stored) as TenantRole[]),
    storedRoles: e.stored,
    system: e.system,
  }));
  const clients: ClientMember[] = [];
  for (const r of (c.data ?? []) as unknown as {
    user_id: string;
    role: string;
    client_org_id: string;
    app_user: UserEmbed | UserEmbed[];
    client_org: { id: string; name: string } | { id: string; name: string }[] | null;
  }[]) {
    if (!isClientRole(r.role)) continue;
    const u = one(r.app_user);
    clients.push({
      userId: r.user_id,
      email: u?.email ?? null,
      displayName: u?.display_name ?? '',
      role: r.role,
      clientOrgId: r.client_org_id,
      clientOrgName: one(r.client_org)?.name ?? '',
    });
  }
  const byName = (a: { email: string | null }, b: { email: string | null }): number =>
    (a.email ?? '~').localeCompare(b.email ?? '~');
  return {
    staff: staff.sort(byName),
    clients: clients.sort((a, b) => a.clientOrgName.localeCompare(b.clientOrgName) || byName(a, b)),
    orgs: (o.data ?? []) as ClientOrg[],
  };
}

/** The database's refusal to remove a tenant's last tenant admin (trigger in 20261018000050_multi_role.sql). */
export const LAST_ADMIN_MESSAGE =
  'A tenant must always have at least one tenant admin. Give another member the Tenant admin role first.';

/** Database errors in plain words. */
export function plainError(error: { readonly message: string; readonly code?: string }): string {
  if (/at least one tenant admin/i.test(error.message)) return LAST_ADMIN_MESSAGE;
  if (error.code === '42501' || /row-level security/i.test(error.message)) {
    return 'You do not have permission to make this change.';
  }
  if (error.code === '23505') return 'The member already has this role.';
  return error.message;
}

/** RLS turns a forbidden update or delete into "0 rows", not an error; report it as one. */
function changed(res: { error: { message: string; code?: string } | null; data: unknown[] | null }): void {
  if (res.error) throw new Error(plainError(res.error));
  if (!res.data || res.data.length === 0) {
    throw new Error('Nothing was changed. You may not have permission, or the member was already removed.');
  }
}

async function audit(
  sb: SupabaseClient,
  tenantId: string,
  actorId: string,
  action: string,
  target: string,
  detail: Record<string, unknown>,
): Promise<void> {
  // Best effort: the change itself has succeeded. A platform admin without a membership cannot insert here.
  await sb.from('audit_event').insert({ tenant_id: tenantId, actor_id: actorId, action, target, detail });
}

/** Give a staff member one more role: a new membership row (RLS: tenant admins and platform admins only). */
export async function addStaffRole(
  sb: SupabaseClient,
  tenantId: string,
  actorId: string,
  member: StaffMember,
  role: TenantRole,
): Promise<void> {
  const res = await sb
    .from('membership')
    .insert({ tenant_id: tenantId, user_id: member.userId, role })
    .select('user_id');
  changed(res);
  await audit(sb, tenantId, actorId, 'membership.role_added', member.userId, { role });
}

/** Take one role away; the member keeps their other roles. The database refuses to remove the last tenant admin. */
export async function removeStaffRole(
  sb: SupabaseClient,
  tenantId: string,
  actorId: string,
  member: StaffMember,
  role: TenantRole,
): Promise<void> {
  const res = await sb
    .from('membership')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('user_id', member.userId)
    .eq('role', member.storedRoles[role] ?? role)
    .select('user_id');
  changed(res);
  await audit(sb, tenantId, actorId, 'membership.role_removed', member.userId, { role });
}

export async function removeStaff(
  sb: SupabaseClient,
  tenantId: string,
  actorId: string,
  member: StaffMember,
): Promise<void> {
  const res = await sb
    .from('membership')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('user_id', member.userId)
    .select('user_id');
  changed(res);
  await audit(sb, tenantId, actorId, 'membership.removed', member.userId, { roles: member.roles });
}

export async function setClientRole(
  sb: SupabaseClient,
  tenantId: string,
  actorId: string,
  member: ClientMember,
  role: ClientRole,
): Promise<void> {
  const res = await sb
    .from('client_membership')
    .update({ role })
    .eq('client_org_id', member.clientOrgId)
    .eq('user_id', member.userId)
    .select('user_id');
  changed(res);
  await audit(sb, tenantId, actorId, 'client_membership.role_changed', member.userId, {
    client_org_id: member.clientOrgId,
    from: member.role,
    to: role,
  });
}

export async function removeClient(
  sb: SupabaseClient,
  tenantId: string,
  actorId: string,
  member: ClientMember,
): Promise<void> {
  const res = await sb
    .from('client_membership')
    .delete()
    .eq('client_org_id', member.clientOrgId)
    .eq('user_id', member.userId)
    .select('user_id');
  changed(res);
  await audit(sb, tenantId, actorId, 'client_membership.removed', member.userId, {
    client_org_id: member.clientOrgId,
    role: member.role,
  });
}

export type InviteRequest =
  | {
      readonly tenant_id: string;
      readonly email: string;
      readonly kind: 'tenant';
      /** One or more; the person's rights are the union. */
      readonly roles: readonly TenantRole[];
      /** "Invite to Teslyn" on the Staff page: the register person linked to the sign-in on acceptance. */
      readonly person_id?: string;
      /**
       * Decision 867: the person's staff register roles, chosen when inviting (required without `person_id`): someone
       * added to the register on acceptance has them from their first sign-in; never guessed.
       */
      readonly register_roles?: readonly RegisterRole[];
    }
  | {
      readonly tenant_id: string;
      readonly email: string;
      readonly kind: 'client';
      readonly role: ClientRole;
      readonly client_org_id: string;
    };

export interface InviteResult {
  readonly status: 'invited' | 'added';
  readonly user_id: string;
  /** Staff roles this invitation wrote (an existing member gains only the ones they did not hold). */
  readonly roles_added?: readonly TenantRole[];
  /** The staff register link (decision 660); absent from an invite service deployed before it. */
  readonly link?: InviteLinkResult | null;
}

/** Call the invite-user Edge Function (service-role work happens there, never in the browser). */
export async function invite(
  sb: SupabaseClient,
  req: InviteRequest,
  redirectTo: string,
): Promise<InviteResult> {
  const { data, error } = await sb.functions.invoke<InviteResult & { error?: string }>('invite-user', {
    body: { ...req, redirect_to: redirectTo },
  });
  if (error) {
    // FunctionsHttpError carries the JSON body with our message in `context`.
    let msg = error.message;
    const ctx = (error as { context?: unknown }).context;
    if (ctx instanceof Response) {
      try {
        const j = (await ctx.json()) as { error?: string };
        if (j.error) msg = j.error;
      } catch {
        /* keep the generic message */
      }
    }
    throw new Error(msg);
  }
  if (!data) throw new Error('The invite service returned no answer.');
  return data;
}
